import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Shield, Loader2, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Clock, Flag, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
/**
 * Background Checks Kanban
 *
 * Columns: Created · Pending · In Review · Flagged · Approved
 * Each card shows talent, Yardstik report link, created-at, and a "Change status"
 * action that calls `/background-checks/admin/{id}/update-status`.
 *
 * Data source: live `/background-checks/admin/pending` (Yardstik-backed) —
 * columns are derived from the `status` + `is_flagged` fields.
 */

const COLUMNS = [
  { key: 'created', label: 'Created', match: (c) => c.status === 'pending' && !c.yardstik_report_id, color: 'bg-gray-50 border-gray-200', icon: Clock, accent: 'text-gray-600' },
  { key: 'pending', label: 'Pending',   match: (c) => c.status === 'pending' && c.yardstik_report_id, color: 'bg-amber-50 border-amber-200', icon: Clock, accent: 'text-amber-700' },
  { key: 'in_progress', label: 'In Review', match: (c) => ['processing','in_progress','in_review'].includes(c.status), color: 'bg-blue-50 border-blue-200', icon: RefreshCw, accent: 'text-blue-700' },
  { key: 'flagged', label: 'Flagged',   match: (c) => c.is_flagged || c.status === 'flagged', color: 'bg-red-50 border-red-200', icon: Flag, accent: 'text-red-700' },
  { key: 'approved', label: 'Approved', match: (c) => ['approved','completed','clear'].includes(c.status), color: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2, accent: 'text-emerald-700' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'rejected', label: 'Rejected' },
];

const BackgroundChecksKanban = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState('approved');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get('/background-checks/admin/pending', { params: { limit: 200 } }),
        api.get('/background-checks/admin/stats'),
      ]);
      setChecks(listRes.data.checks || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load background checks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = (c) => {
    setSelected(c);
    setEditStatus(c.status || 'approved');
    setNotes(c.flag_reason || '');
  };

  const submitChange = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/background-checks/admin/${selected.id}/update-status`, null, {
        params: { new_status: editStatus, notes: notes.trim() || undefined },
      });
      toast.success('Status updated');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="bgcheck-kanban-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-600" />
              Background Checks
            </h1>
            {stats?.service_status?.enabled === false && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                <AlertTriangle className="w-3 h-3 mr-1" /> Mock mode — Yardstik API not enabled
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-bgcheck-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-4">
            {[
              ['Total', stats.checks.total, 'bg-gray-100 text-gray-800'],
              ['Pending', stats.checks.pending, 'bg-amber-100 text-amber-800'],
              ['In Review', stats.checks.in_progress, 'bg-blue-100 text-blue-800'],
              ['Approved', stats.checks.approved, 'bg-emerald-100 text-emerald-800'],
              ['Flagged', stats.checks.flagged, 'bg-red-100 text-red-800'],
              ['Expired', stats.checks.expired, 'bg-purple-100 text-purple-800'],
            ].map(([label, val, cls]) => (
              <div key={label} className={`${cls} rounded p-2 text-center`}>
                <div className="text-xl font-semibold">{val}</div>
                <div className="text-[11px] uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="bgcheck-kanban">
            {COLUMNS.map((col) => {
              const items = checks.filter(col.match);
              const Icon = col.icon;
              return (
                <div key={col.key} className={`rounded border ${col.color} p-2`} data-testid={`bgcheck-col-${col.key}`}>
                  <div className={`flex items-center gap-1.5 mb-2 ${col.accent}`}>
                    <Icon className="w-4 h-4" />
                    <div className="text-sm font-medium">{col.label}</div>
                    <Badge variant="outline" className="ml-auto text-xs">{items.length}</Badge>
                  </div>
                  <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                    {items.length === 0 && (
                      <p className="text-xs text-gray-400 italic text-center py-3">Empty</p>
                    )}
                    {items.map((c) => (
                      <Card
                        key={c.id}
                        className="cursor-pointer hover:shadow transition bg-white"
                        onClick={() => openDetail(c)}
                        data-testid={`bgcheck-card-${c.id}`}
                      >
                        <CardContent className="p-3 space-y-1.5">
                          <div className="flex items-center gap-2">
                            {c.profile_photo ? (
                              <img src={c.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gray-200" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">{c.talent_name}</div>
                              <div className="text-[11px] text-gray-500 truncate">{c.talent_email}</div>
                            </div>
                          </div>
                          {c.flag_reason && (
                            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-1 line-clamp-2">
                              {c.flag_reason}
                            </div>
                          )}
                          <div className="flex items-center justify-between text-[10px] text-gray-500">
                            <span>{c.created_at ? formatDate(c.created_at) : '—'}</span>
                            {c.yardstik_report_id && (
                              <span className="flex items-center gap-0.5 text-cyan-600">
                                <ExternalLink className="w-2.5 h-2.5" />
                                Yardstik
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail / update-status modal */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="bgcheck-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.talent_name}</DialogTitle>
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="text-xs text-gray-600 space-y-1">
                <div><span className="font-medium">Email:</span> {selected.talent_email}</div>
                <div><span className="font-medium">Yardstik ID:</span> {selected.yardstik_candidate_id || '—'}</div>
                <div><span className="font-medium">Report ID:</span> {selected.yardstik_report_id || '—'}</div>
                <div><span className="font-medium">Created:</span> {new Date(selected.created_at).toLocaleString()}</div>
                {selected.completed_at && (
                  <div><span className="font-medium">Completed:</span> {new Date(selected.completed_at).toLocaleString()}</div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Update status</label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger data-testid="bgcheck-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Notes / flag reason (required if flagging or rejecting)</label>
                <Textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Escalated by Yardstik; requires manual review of driving history."
                  data-testid="bgcheck-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} disabled={saving}>Cancel</Button>
            <Button
              onClick={submitChange}
              disabled={saving || (['flagged','rejected'].includes(editStatus) && !notes.trim())}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="bgcheck-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackgroundChecksKanban;
